import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../api/ApiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((token) => {
    setAccessToken(token);
    api.setToken(token);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.refreshToken();
      if (data?.accessToken) {
        applyToken(data.accessToken);
        setUser(data.user);
        return true;
      }
    } catch {
      // refresh failed — user is not logged in
    }
    return false;
  }, [applyToken]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async ({ email, password }) => {
    const data = await api.login({ email, password });
    applyToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, [applyToken]);

  const register = useCallback(async ({ email, password, name }) => {
    const data = await api.register({ email, password, name });
    applyToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, [applyToken]);

  const loginWithGoogle = useCallback(async (credential, access_token) => {
    const data = await api.googleAuth(credential, access_token);
    applyToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, [applyToken]);

  // Called by /oauth/callback page after GitHub redirect
  const handleOAuthToken = useCallback(async (token) => {
    applyToken(token);
    try {
      const data = await api.getMe();
      setUser(data.user);
    } catch {
      // fallback: trigger a refresh which re-reads the httpOnly cookie
      await refresh();
    }
  }, [applyToken, refresh]);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    api.setToken(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const value = {
    user, accessToken, loading,
    login, register, loginWithGoogle, handleOAuthToken, logout, refresh, updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
